import type { AttackKind, Combatant } from './simulation'
import type { BodyRegion, OrganRole } from './lifeOmega'

export type TargetZoneId='head'|'chest'|'abdomen'|'core'|'left-arm'|'right-arm'|'left-thigh'|'right-thigh'|'left-shin'|'right-shin'
export type TargetZone={
  id:TargetZoneId
  bodyRegion:BodyRegion
  y:number
  radiusX:number
  radiusZ:number
  organRole:OrganRole
  reactionBias:'neural'|'respiration'|'circulation'|'energy'|'defense'|'locomotion'
}
export type AttackAimProfile={
  kind:AttackKind
  targets:TargetZoneId[]
  depthReach:number
  contactLead:number
  aimAssist:number
}
export type StrikeContact={
  targetZone:TargetZoneId
  bodyRegion:BodyRegion
  organRole:OrganRole
  reactionBias:TargetZone['reactionBias']
  x:number
  y:number
  z:number
  normalX:number
  normalZ:number
  forceX:number
  forceZ:number
  activeProgress:number
  precision:number
}
export const TARGET_ZONES:Record<TargetZoneId,TargetZone>={
  head:{id:'head',bodyRegion:'head',y:1.63,radiusX:.24,radiusZ:.24,organRole:'control',reactionBias:'neural'},
  chest:{id:'chest',bodyRegion:'torso',y:1.28,radiusX:.34,radiusZ:.28,organRole:'respiration',reactionBias:'respiration'},
  abdomen:{id:'abdomen',bodyRegion:'torso',y:.98,radiusX:.31,radiusZ:.27,organRole:'circulation',reactionBias:'circulation'},
  core:{id:'core',bodyRegion:'torso',y:1.08,radiusX:.26,radiusZ:.24,organRole:'energy',reactionBias:'energy'},
  'left-arm':{id:'left-arm',bodyRegion:'left-arm',y:1.28,radiusX:.21,radiusZ:.21,organRole:'locomotion',reactionBias:'defense'},
  'right-arm':{id:'right-arm',bodyRegion:'right-arm',y:1.28,radiusX:.21,radiusZ:.21,organRole:'locomotion',reactionBias:'defense'},
  'left-thigh':{id:'left-thigh',bodyRegion:'left-leg',y:.65,radiusX:.25,radiusZ:.24,organRole:'locomotion',reactionBias:'locomotion'},
  'right-thigh':{id:'right-thigh',bodyRegion:'right-leg',y:.65,radiusX:.25,radiusZ:.24,organRole:'locomotion',reactionBias:'locomotion'},
  'left-shin':{id:'left-shin',bodyRegion:'left-leg',y:.32,radiusX:.19,radiusZ:.2,organRole:'locomotion',reactionBias:'locomotion'},
  'right-shin':{id:'right-shin',bodyRegion:'right-leg',y:.32,radiusX:.19,radiusZ:.2,organRole:'locomotion',reactionBias:'locomotion'},
}

export const ATTACK_AIM:Record<AttackKind,AttackAimProfile>={
  light:{kind:'light',targets:['chest','right-arm','left-arm'],depthReach:.82,contactLead:.08,aimAssist:.8},
  heavy:{kind:'heavy',targets:['head','chest','left-thigh','right-thigh'],depthReach:1.05,contactLead:.12,aimAssist:.92},
  special:{kind:'special',targets:['core','chest','head'],depthReach:1.65,contactLead:.16,aimAssist:1},
  throw:{kind:'throw',targets:['chest','abdomen'],depthReach:.7,contactLead:.04,aimAssist:.72},
}

export function targetZoneForAttack(kind:AttackKind,serial:number){
  const candidates=ATTACK_AIM[kind].targets
  return TARGET_ZONES[candidates[Math.abs(serial)%candidates.length]]
}
export function strikeBroadphase(attacker:Combatant,defender:Combatant,kind:AttackKind,forward:number,halfWidth:number){
  const profile=ATTACK_AIM[kind],dir=Math.sign(defender.x-attacker.x)||1
  const center=attacker.x+dir*forward,reach=halfWidth+defender.hurtHalfWidth
  return Math.abs(defender.x-center)<=reach&&Math.abs(defender.z-attacker.z)<=profile.depthReach
}

export function resolveStrikeContact(
  attacker:Combatant,
  defender:Combatant,
  kind:AttackKind,
  serial:number,
  activeProgress=.5,
  lockedTarget?:TargetZoneId,
):StrikeContact{
  const target=lockedTarget?TARGET_ZONES[lockedTarget]:targetZoneForAttack(kind,serial)
  const dir=Math.sign(defender.x-attacker.x)||1
  const dz=defender.z-attacker.z
  const depthError=Math.min(1,Math.abs(dz)/Math.max(.001,ATTACK_AIM[kind].depthReach))
  const surface=defender.hurtHalfWidth*.72+target.radiusX*.28
  const precision=Math.max(0,Math.min(1,(1-depthError*.42)*ATTACK_AIM[kind].aimAssist))
  return{
    targetZone:target.id,
    bodyRegion:target.bodyRegion,
    organRole:target.organRole,
    reactionBias:target.reactionBias,
    x:defender.x-dir*surface,
    y:defender.y+target.y,
    z:defender.z-dz*.08,
    normalX:-dir,
    normalZ:Math.max(-1,Math.min(1,-dz)),
    forceX:dir,
    forceZ:Math.max(-.55,Math.min(.55,dz*.22)),
    activeProgress:Math.max(0,Math.min(1,activeProgress)),
    precision,
  }
}

export function contactDistance(contact:StrikeContact,defender:Combatant){
  return Math.hypot(contact.x-defender.x,contact.z-defender.z)
}
